import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../app/guards/RouteGuards';
import { RoleBadge } from '../ui/RoleBadge';
import { WorkspaceService } from '../../services/workspace/workspace.service';
import {
  Card,
} from '../ui/Card';
import { Button } from '../ui/Button';
import { Users, Lock, Bell, Package, CreditCard, LogOut, Shield, Wrench, ChevronRight, Copy, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Role, Permission as PermissionType, ROLE_PERMISSIONS, getRolePermissions } from '../../services/permissions/permissions.service';

type SettingsTab =
  | 'account'
  | 'workspace'
  | 'members'
  | 'roles'
  | 'permissions'
  | 'security'
  | 'billing'
  | 'applications';

export function SettingsView() {
  const { user, activeWorkspace, logout } = useAuth();
  const { isAdminOrOwner, can, role, permissions } = usePermissions();
  const [activeTab, setActiveTab] = useState<SettingsTab>('account');

  const tabs = [
    { id: 'account' as const, label: 'Minha Conta', icon: Users },
    { id: 'workspace' as const, label: 'Workspace', icon: Package },
    ...(can('workspace.manage_members')
      ? [{ id: 'members' as const, label: 'Membros', icon: Users }]
      : []),
    ...(can('workspace.manage_roles')
      ? [
          { id: 'roles' as const, label: 'Funções & Permissões', icon: Shield },
        ]
      : []),
    { id: 'security' as const, label: 'Segurança', icon: Lock },
    { id: 'billing' as const, label: 'Faturamento', icon: CreditCard },
    { id: 'applications' as const, label: 'Aplicativos', icon: Package },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return <AccountSettings user={user} role={role} />;
      case 'workspace':
        return <WorkspaceSettings workspace={activeWorkspace} />;
      case 'members':
        return (
          <PermissionGuard permission="workspace.manage_members">
            <MembersSettings workspaceId={activeWorkspace?.id} />
          </PermissionGuard>
        );
      case 'roles':
        return (
          <PermissionGuard permission="workspace.manage_roles">
            <RolesSettings />
          </PermissionGuard>
        );
      case 'security':
        return <SecuritySettings />;
      case 'billing':
        return <BillingSettings workspace={activeWorkspace} />;
      case 'applications':
        return <ApplicationsSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-[#111111] mb-2">Configurações</h1>
        <p className="text-[#64748B]">Gerencie sua conta, workspace e preferências</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Tabs */}
        <div className="w-full lg:w-48">
          <div className="space-y-2 sticky top-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                    activeTab === tab.id
                      ? 'bg-[#111111] text-white'
                      : 'text-[#64748B] hover:bg-[#F3F4F6] hover:text-[#111111]'
                  }`}
                >
                  <Icon size={18} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
            <div className="h-px bg-[#E5E7EB] my-4" />
            <button
              onClick={() => {
                if (confirm('Tem certeza que deseja sair?')) {
                  logout();
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left text-red-600 hover:bg-red-50"
            >
              <LogOut size={18} />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderTabContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function AccountSettings({ user, role }: any) {
  return (
    <Card className="p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#111111] mb-4">Minha Conta</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-[#64748B] mb-2">E-mail</label>
            <div className="px-4 py-2 bg-[#F3F4F6] rounded-lg text-[#111111]">
              {user?.email}
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-[#64748B] mb-2">Nome</label>
            <div className="px-4 py-2 bg-[#F3F4F6] rounded-lg text-[#111111]">
              {user?.displayName || 'Não informado'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-[#64748B] mb-2">Função no Workspace</label>
            <div className="px-4 py-2 bg-[#F3F4F6] rounded-lg">
              <RoleBadge role={role} />
            </div>
          </div>
        </div>
      </div>
      <Button className="w-full md:w-auto">Editar Perfil</Button>
    </Card>
  );
}

function WorkspaceSettings({ workspace }: any) {
  return (
    <Card className="p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#111111] mb-4">Configurações do Workspace</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-[#64748B] mb-2">Nome</label>
            <input
              type="text"
              defaultValue={workspace?.name}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#111111]"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#64748B] mb-2">Descrição</label>
            <textarea
              defaultValue={workspace?.description}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#111111]"
              rows={4}
            />
          </div>
        </div>
      </div>
      <Button className="w-full md:w-auto">Salvar Alterações</Button>
    </Card>
  );
}

function MembersSettings({ workspaceId }: { workspaceId?: string | number }) {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  React.useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    WorkspaceService.getMembers(workspaceId)
      .then((data) => setMembers(data))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  return (
    <Card className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#111111]">Membros do Workspace</h2>
        <Button className="w-auto">Convidar Membro</Button>
      </div>
      <p className="text-[#64748B]">Gerencie quem tem acesso a este workspace</p>
      {loading ? (
        <div className="py-8 text-center text-[#64748B]">Carregando membros...</div>
      ) : (
        <div className="space-y-3">
          {members.length === 0 && (
            <div className="py-4 text-sm text-[#999999]">Nenhum membro encontrado.</div>
          )}
          {members.map((member: any) => (
            <div key={member.id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E5E7EB] flex items-center justify-center text-[#111111] font-bold text-xs">
                  {member.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-medium text-[#111111]">{member.name || member.email}</p>
                  <p className="text-xs text-[#64748B]">{member.email}</p>
                </div>
              </div>
              <RoleBadge role={member.role || 'MEMBER'} size="sm" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RolesSettings() {
  const { role, permissions } = usePermissions();

  const allRoles: { role: Role; label: string; permissions: PermissionType[] }[] = [
    { role: 'OWNER' as Role, label: 'Proprietário', permissions: ROLE_PERMISSIONS.OWNER },
    { role: 'ADMIN' as Role, label: 'Administrador', permissions: ROLE_PERMISSIONS.ADMIN },
    { role: 'MANAGER' as Role, label: 'Gerente', permissions: ROLE_PERMISSIONS.MANAGER },
    { role: 'MEMBER' as Role, label: 'Membro', permissions: ROLE_PERMISSIONS.MEMBER },
    { role: 'VIEWER' as Role, label: 'Visualizador', permissions: ROLE_PERMISSIONS.VIEWER },
  ];

  const currentRolePerms = getRolePermissions(role || 'VIEWER');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#111111] mb-2">Funções & Permissões</h2>
        <p className="text-[#64748B] mb-4">
          Sua função atual neste workspace: <RoleBadge role={role || 'VIEWER'} />
        </p>
      </div>

      <Card className="p-6">
        <h3 className="font-bold text-[#111111] mb-4">Permissões da sua função</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {currentRolePerms.map((p) => (
            <div key={p} className="flex items-center gap-2 text-sm text-[#111111]">
              <Check size={14} className="text-emerald-500" />
              {p}
            </div>
          ))}
          {currentRolePerms.length === 0 && (
            <p className="text-sm text-[#64748B]">Sem permissões específicas.</p>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-bold text-[#111111] mb-4">Resumo por Função</h3>
        <div className="space-y-4">
          {allRoles.map(({ role: r, label, permissions: perms }) => (
            <div key={r} className="border border-[#E5E7EB] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <RoleBadge role={r} />
                <span className="font-bold text-[#111111]">{label}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 text-xs text-[#64748B]">
                {perms.map((p) => (
                  <span key={p} className="px-2 py-1 bg-[#F3F4F6] rounded">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SecuritySettings() {
  return (
    <Card className="p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#111111] mb-4">Segurança</h2>
        <div className="space-y-4">
          <Button variant="outline" className="w-full md:w-auto">
            Alterar Senha
          </Button>
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Autenticação de dois fatores estará disponível em breve.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function BillingSettings({ workspace }: any) {
  return (
    <Card className="p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#111111] mb-4">Faturamento</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-[#64748B] mb-2">Plano Atual</label>
            <div className="px-4 py-2 bg-[#F3F4F6] rounded-lg text-[#111111] font-bold">
              {workspace?.plan || 'Padrão'} - R$ {workspace?.monthlyPrice || '0,00'}/mês
            </div>
          </div>
          <p className="text-sm text-[#999999]">
            Funcionalidade de upgrade de plano em desenvolvimento
          </p>
        </div>
      </div>
    </Card>
  );
}

function ApplicationsSettings() {
  const { role, permissions } = usePermissions();

  return (
    <Card className="p-8 space-y-6">
      <div>
        <h2 className="text-2px font-bold text-[#111111] mb-4">Aplicativos do Workspace</h2>
        <p className="text-[#64748B] mb-4">
          Sua função ({role || 'VIEWER'}) determina quais aplicativos estão disponíveis.
        </p>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg">
          <div className="flex items-center gap-3">
            <Package size={20} className="text-[#64748B]" />
            <div>
              <p className="font-medium text-[#111111]">CRM</p>
              <p className="text-xs text-[#64748B]">Gestão de relacionamento com clientes</p>
            </div>
          </div>
          <Button className="w-auto text-xs">Configurar</Button>
        </div>
        <div className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg">
          <div className="flex items-center gap-3">
            <Package size={20} className="text-[#64748B]" />
            <div>
              <p className="font-medium text-[#111111]">ERP</p>
              <p className="text-xs text-[#64748B]">Gestão empresarial: produtos, vendas e financeiro</p>
            </div>
          </div>
          <Button className="w-auto text-xs">Configurar</Button>
        </div>
      </div>
    </Card>
  );
}
